import React from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  addDays,
  classRuns,
  diffDays,
  formatDay,
  formatISO,
  formatRange,
  parseDate,
} from "./plannerUtils";
import { WEEKDAY_LABELS, colorFor, initials } from "./plannerConstants";

const inRange = (d, start, end) => d >= start && d <= end;

/** Greedy lane assignment so overlapping bars in a week stack. */
const assignLanes = (segs) => {
  const ends = [];
  return segs.map((g) => {
    let lane = ends.findIndex((e) => e < g.c0);
    if (lane < 0) {
      lane = ends.length;
      ends.push(g.c1);
    } else ends[lane] = g.c1;
    return { ...g, lane };
  });
};

export default function MonthGrid({
  month,
  classes,
  conflictIds,
  semester,
  today,
  selectedId,
  placing,
  hoverDate,
  previewRange,
  canEdit,
  instructorNames,
  onSelect,
  onHover,
  onDropAt,
}) {
  const first = new Date(month.y, month.m, 1);
  const gridStart = addDays(formatISO(first), -first.getDay());
  const lastDay = formatISO(new Date(month.y, month.m + 1, 0));
  const weeks = [];
  for (let w = gridStart; w <= lastDay; w = addDays(w, 7)) weeks.push(w);

  const bars = classes.flatMap((sc) =>
    classRuns(sc).map((run, i) => ({ sc, run, key: `${sc.id}-${i}` })),
  );

  return (
    <div className="sp-mv" data-testid="planner-month-grid">
      <div className="sp-mv-dh">
        {WEEKDAY_LABELS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      {weeks.map((ws) => {
        const we = addDays(ws, 6);
        const segs = assignLanes(
          bars
            .filter(({ run }) => run.start <= we && run.end >= ws)
            .sort(
              (a, b) =>
                a.run.start.localeCompare(b.run.start) ||
                b.run.end.localeCompare(a.run.end),
            )
            .map((b) => ({
              ...b,
              c0: Math.max(0, diffDays(ws, b.run.start)),
              c1: Math.min(6, diffDays(ws, b.run.end)),
            })),
        );
        const laneCount = segs.reduce((n, s) => Math.max(n, s.lane + 1), 0);
        return (
          <div
            key={ws}
            className="sp-wk"
            style={{ gridTemplateRows: `30px repeat(${laneCount},26px) minmax(14px,1fr)` }}
          >
            {[0, 1, 2, 3, 4, 5, 6].map((i) => {
              const d = addDays(ws, i);
              const inSem = inRange(d, semester.startDate, semester.endDate);
              const pv = previewRange && inRange(d, previewRange.start, previewRange.end);
              const cls = [
                "sp-cell",
                parseDate(d).getMonth() !== month.m ? "out" : "",
                inSem ? "" : "off",
                pv ? (previewRange.ok ? "pv" : "pv bad") : "",
                placing && inSem && canEdit ? "pick" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <div
                  key={d}
                  className={cls}
                  style={{ gridColumn: i + 1 }}
                  data-date={d}
                  {...(placing && inSem && canEdit
                    ? {
                        role: "button",
                        tabIndex: 0,
                        "aria-label": `Place class starting ${formatDay(d, { weekday: "long", month: "long", day: "numeric" })}`,
                        onFocus: () => onHover(d),
                        onKeyDown: (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onDropAt(d);
                          }
                        },
                      }
                    : {})}
                  data-testid={`planner-day-${d}`}
                  onDragOver={(e) => {
                    if (inSem && canEdit) {
                      e.preventDefault();
                      if (hoverDate !== d) onHover(d);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (inSem && canEdit) onDropAt(d);
                  }}
                  onMouseEnter={() => placing && onHover(inSem ? d : null)}
                  onClick={() => {
                    if (placing) {
                      if (inSem && canEdit) onDropAt(d);
                    } else onSelect(null);
                  }}
                >
                  <span className={`sp-dn${d === today ? " td" : ""}`}>
                    {parseDate(d).getDate() === 1 ? formatDay(d) : parseDate(d).getDate()}
                  </span>
                  {d === semester.startDate && <span className="sp-mk">Starts</span>}
                  {d === semester.endDate && <span className="sp-mk">Ends</span>}
                </div>
              );
            })}
            {segs.map(({ sc, run, key, c0, c1, lane }) => {
              const cf = conflictIds.has(sc.id);
              const name = instructorNames[sc.instructorId];
              return (
                <button
                  key={key}
                  type="button"
                  className={[
                    "sp-bar",
                    run.start < ws ? "cl" : "",
                    run.end > we ? "cr" : "",
                    selectedId === sc.id ? "on" : "",
                    cf ? "cf" : "",
                    sc.instructorId ? "" : "ua",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    "--c": colorFor(sc.category),
                    gridColumn: `${c0 + 1} / ${c1 + 2}`,
                    gridRow: lane + 2,
                  }}
                  data-testid={`planner-bar-${sc.id}`}
                  title={`${sc.title} · ${formatRange(run.start, run.end)}`}
                  aria-label={`${sc.title}, ${formatRange(run.start, run.end)}${sc.instructorId ? "" : ", no instructor"}${cf ? ", scheduling conflict" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(sc.id);
                  }}
                >
                  {cf ? <WarningAmberIcon sx={{ fontSize: 12 }} /> : <i />}
                  <span className="sp-bn">{sc.title}</span>
                  {c1 - c0 >= 1 && (
                    <span className={`sp-pav${name ? "" : " ua"}`}>
                      {name ? initials(name) : "?"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
